export type UrlValidationMode = "allow_all" | "allow_only" | "block";

export type UrlValidation =
  | { mode: "allow_all" }
  | { mode: "allow_only"; allowedDomains: string[] }
  | { mode: "block"; blockedDomains: string[] };

type UrlValidationResponse = {
  mode?: unknown;
  allowedDomains?: unknown;
  blockedDomains?: unknown;
};

const DOMAIN_MAX_LENGTH = 253;
const DOMAIN_LABEL_MAX_LENGTH = 63;
const DOMAIN_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/**
 * Normalizes a domain for storage and comparison.
 *
 * @param value - Domain value to normalize.
 * @returns The trimmed, lower-case domain without a leading `www.`.
 */
export const normalizeDomain = (value: string): string =>
  value.trim().toLowerCase().replace(/^www\./, "");

/**
 * Checks whether a value is a valid domain name.
 *
 * @param value - Domain value to validate.
 * @returns `true` when the value contains valid domain labels.
 */
export const isValidDomain = (value: string): boolean => {
  const domain = normalizeDomain(value);

  if (
    !domain ||
    domain.length > DOMAIN_MAX_LENGTH ||
    domain.startsWith(".") ||
    domain.endsWith(".")
  ) {
    return false;
  }

  const labels = domain.split(".");
  return (
    labels.length > 1 &&
    labels.every(
      (label) =>
        label.length > 0 &&
        label.length <= DOMAIN_LABEL_MAX_LENGTH &&
        DOMAIN_LABEL_PATTERN.test(label)
    )
  );
};

/**
 * Converts an unknown domain collection into unique, valid domain values.
 *
 * @param value - Candidate domain collection.
 * @returns The normalized domains, or an empty array for invalid input.
 */
const normalizeDomains = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return [
    ...new Set(
      value
        .filter((domain): domain is string => typeof domain === "string")
        .map(normalizeDomain)
        .filter(isValidDomain)
    ),
  ];
};

/**
 * Builds the URL validation payload for the selected domain mode.
 *
 * @param mode - Domain validation mode.
 * @param domains - Domains used by restricted validation modes.
 * @returns A normalized URL validation payload.
 */
export const buildUrlValidation = (
  mode: UrlValidationMode,
  domains: string[] = []
): UrlValidation => {
  if (mode === "allow_only") {
    return {
      mode,
      allowedDomains: normalizeDomains(domains),
    };
  }

  if (mode === "block") {
    return {
      mode,
      blockedDomains: normalizeDomains(domains),
    };
  }

  return { mode: "allow_all" };
};

/**
 * Normalizes URL validation data from API responses.
 *
 * @param value - Candidate URL validation configuration.
 * @returns A normalized URL validation payload, defaulting to allow all.
 */
export const normalizeUrlValidation = (value: unknown): UrlValidation => {
  if (!value || typeof value !== "object") return { mode: "allow_all" };

  const response = value as UrlValidationResponse;

  if (response.mode === "allow_only") {
    return buildUrlValidation(
      response.mode,
      normalizeDomains(response.allowedDomains)
    );
  }

  if (response.mode === "block") {
    return buildUrlValidation(
      response.mode,
      normalizeDomains(response.blockedDomains)
    );
  }

  return { mode: "allow_all" };
};

/**
 * Gets the domain list associated with a URL validation configuration.
 *
 * @param value - Candidate URL validation configuration.
 * @returns A copy of the configured allowed or blocked domains.
 */
export const getUrlValidationDomains = (value: unknown): string[] => {
  const validation = normalizeUrlValidation(value);

  if (validation.mode === "allow_only") return [...validation.allowedDomains];
  if (validation.mode === "block") return [...validation.blockedDomains];
  return [];
};

/**
 * Checks whether a URL validation configuration has all required domains.
 *
 * @param value - Candidate URL validation configuration.
 * @returns `true` for allow-all mode or a restricted mode with domains.
 */
export const isUrlValidationComplete = (value: unknown): boolean => {
  const validation = normalizeUrlValidation(value);
  return (
    validation.mode === "allow_all" ||
    getUrlValidationDomains(validation).length > 0
  );
};

/**
 * Recursively checks builder elements and slide data for incomplete URL settings.
 *
 * @param value - Builder element, slide, or collection to inspect.
 * @returns `true` when a URL field has a restricted mode without domains.
 */
export const hasIncompleteUrlValidation = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some(hasIncompleteUrlValidation);
  }

  if (!value || typeof value !== "object") return false;

  const candidate = value as {
    dataType?: unknown;
    urlValidation?: unknown;
    slideData?: unknown;
  };

  if (
    candidate.dataType === "URL" &&
    !isUrlValidationComplete(candidate.urlValidation)
  ) {
    return true;
  }

  return hasIncompleteUrlValidation(candidate.slideData);
};
